import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { patch } from "../../api";
import { Checkbox, Field, Panel, PanelHeader, SelectField, TextAreaField } from "../../components/ui";
import { SettingsForm, type SettingsResponse, useDraft } from "./common";

const TIMEZONES = ["America/Chicago", "America/New_York", "America/Denver", "America/Phoenix", "America/Los_Angeles", "America/Anchorage", "Pacific/Honolulu"];
const CONTACT_KEYS = ["profile.phone", "profile.phonePublic", "profile.email", "profile.emailPublic", "profile.website", "profile.instagram", "profile.facebook", "profile.tagline", "profile.address", "profile.hours"] as const;
const INVENTORY_KEYS = ["inventory.invoiceReading"] as const;
const CHART_KEYS = ["charts.aiCleanup"] as const;

export function ClinicTab({ data }: { data: SettingsResponse }) {
  const qc = useQueryClient();
  const [clinic, setClinic] = useState({ name: "", timezone: "", slug: "" });
  const [notice, setNotice] = useState<string | null>(null);
  useEffect(() => setClinic({ name: data.clinic.name, timezone: data.clinic.timezone, slug: data.clinic.slug ?? "" }), [data.clinic]);
  const saveClinic = useMutation({
    mutationFn: () => patch<SettingsResponse>("/api/clinic", { name: clinic.name, timezone: clinic.timezone, ...(clinic.slug ? { slug: clinic.slug } : {}) }),
    onSuccess: async (d) => {
      qc.setQueryData(["settings"], d);
      await qc.invalidateQueries({ queryKey: ["me"] });
      setNotice("Clinic details saved.");
    },
  });
  const clinicDirty = clinic.name !== data.clinic.name || clinic.timezone !== data.clinic.timezone || clinic.slug !== (data.clinic.slug ?? "");

  const contact = useDraft(data.settings, CONTACT_KEYS, "Contact details saved.");
  const v = contact.value;
  const inventory = useDraft(data.settings, INVENTORY_KEYS, "Inventory settings saved.");
  const charts = useDraft(data.settings, CHART_KEYS, "Chart settings saved.");

  return (
    <div className="flex flex-col gap-6">
      <Panel>
        <PanelHeader title="Clinic" />
        <SettingsForm onSave={() => saveClinic.mutate()} saving={saveClinic.isPending} error={saveClinic.error} notice={notice} dirty={clinicDirty}>
          <Field label="Clinic name" required value={clinic.name} onChange={(e) => (setNotice(null), setClinic({ ...clinic, name: e.target.value }))} />
          <SelectField
            label="Time zone"
            value={clinic.timezone}
            onChange={(e) => (setNotice(null), setClinic({ ...clinic, timezone: e.target.value }))}
            hint="Used for schedules, reminders, and the audit log."
          >
            {[...new Set([clinic.timezone, ...TIMEZONES])].filter(Boolean).map((tz) => (
              <option key={tz} value={tz}>
                {tz.replace("America/", "").replace("Pacific/", "").replace(/_/g, " ")}
              </option>
            ))}
          </SelectField>
          <Field
            label="Link name"
            value={clinic.slug}
            onChange={(e) => (setNotice(null), setClinic({ ...clinic, slug: e.target.value.toLowerCase() }))}
            pattern="[a-z0-9]+(-[a-z0-9]+)*"
            minLength={3}
            maxLength={40}
            hint={`Used in your public links, like /v1/public/clinics/${clinic.slug || "your-clinic"}/website. Lowercase letters, numbers, and dashes.`}
          />
        </SettingsForm>
      </Panel>

      <Panel>
        <PanelHeader title="Contact details" description="Shown to clients on booking pages and the public menu, unless you keep them internal." />
        <SettingsForm onSave={contact.save} saving={contact.saving} error={contact.error} notice={contact.notice} dirty={contact.dirty}>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Field label="Phone" type="tel" value={v["profile.phone"] ?? ""} onChange={(e) => contact.set("profile.phone", e.target.value || null)} />
              <Checkbox label="Show publicly" checked={v["profile.phonePublic"]} onChange={(on) => contact.set("profile.phonePublic", on)} />
            </div>
            <div>
              <Field label="Email" type="email" value={v["profile.email"] ?? ""} onChange={(e) => contact.set("profile.email", e.target.value || null)} />
              <Checkbox label="Show publicly" checked={v["profile.emailPublic"]} onChange={(on) => contact.set("profile.emailPublic", on)} />
            </div>
          </div>
          <Field label="Website" type="url" placeholder="https://" value={v["profile.website"] ?? ""} onChange={(e) => contact.set("profile.website", e.target.value || null)} />
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Instagram" type="url" placeholder="https://instagram.com/…" value={v["profile.instagram"] ?? ""} onChange={(e) => contact.set("profile.instagram", e.target.value || null)} />
            <Field label="Facebook" type="url" placeholder="https://facebook.com/…" value={v["profile.facebook"] ?? ""} onChange={(e) => contact.set("profile.facebook", e.target.value || null)} />
          </div>
          <Field label="Tagline" maxLength={120} value={v["profile.tagline"] ?? ""} onChange={(e) => contact.set("profile.tagline", e.target.value || null)} hint="A line under your name at the bottom of client emails. Your website and social links go there too." />
          <Field label="Address" maxLength={200} value={v["profile.address"] ?? ""} onChange={(e) => contact.set("profile.address", e.target.value || null)} hint="For your website (the website feed)." />
          <TextAreaField label="Opening hours" rows={3} maxLength={400} value={v["profile.hours"] ?? ""} onChange={(e) => contact.set("profile.hours", e.target.value || null)} hint={"As you'd like them shown on your website, e.g. \"Mon–Fri 9–6\" on one line, \"Sat 10–2\" on the next."} />
        </SettingsForm>
      </Panel>

      <Panel>
        <PanelHeader title="Inventory" />
        <SettingsForm onSave={inventory.save} saving={inventory.saving} error={inventory.error} notice={inventory.notice} dirty={inventory.dirty}>
          <Checkbox
            label="Read supplier invoices with AI"
            description="On Record purchase, staff can upload an invoice PDF, screenshot, or photo to fill in the form. Files are read by Claude on Amazon Bedrock under the clinic's AWS agreement; nothing is saved until staff review and save."
            checked={inventory.value["inventory.invoiceReading"]}
            onChange={(on) => inventory.set("inventory.invoiceReading", on)}
          />
        </SettingsForm>
      </Panel>

      <Panel>
        <PanelHeader title="Charts" />
        <SettingsForm onSave={charts.save} saving={charts.saving} error={charts.error} notice={charts.notice} dirty={charts.dirty}>
          <Checkbox
            label="AI Cleanup for the provider narrative"
            description="A button under the narrative that rewrites it clearly and professionally, keeping every detail. Only the narrative is sent (no name or other chart details), to Claude on Amazon Bedrock under the clinic's AWS agreement. The nurse reviews it and chooses whether to use it."
            checked={charts.value["charts.aiCleanup"]}
            onChange={(on) => charts.set("charts.aiCleanup", on)}
          />
        </SettingsForm>
      </Panel>
    </div>
  );
}
